import React, {
  useCallback,
  useContext,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { map, extend } from 'underscore';
import classnames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  IconButton,
  TextField,
} from '@bingads-webui-react/fabric-control';
import { FluentImagePicker } from '@bingads-webui-campaign-react/fluent-image-picker';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';

import { getSearchFieldStyle, getRootStyles } from './styles/index';

const PAGE_SIZE = 20;
const ASSET_TYPE_IMAGES = 1;

export const InvestImageView = ({
  getInvestImages,
  onChangeImageIdSelection,
  enableMultipleSelection,
  imageCellWidth,
  imageCellHeight,
  images,
  setImages,
  hasMore,
  setHasMore,
  skip,
  setSkip,
  isLoadingFirstPage,
  setIsLoadingFirstPage,
  searchQuery,
  setSearchQuery,
}) => {
  const i18n = useContext(I18nContext);
  const searchFieldClasses = useDefaultStyles(getSearchFieldStyle);
  const { investImageRoot } = useDefaultStyles(getRootStyles);

  const loadImages = useCallback(({ creativeId = searchQuery, $skip = skip, $query = {} }) => {
    if ($skip === 0) {
      setIsLoadingFirstPage(true);
    }
    const queryParams = {
      appName: 'CampaignUI',
      assetTypeId: ASSET_TYPE_IMAGES,
      $skip,
      $top: PAGE_SIZE,
    };
    if (creativeId) {
      queryParams.creativeId = creativeId;
    }
    getInvestImages({
      customQueries: extend(queryParams, $query),
    }).then((data) => {
      setHasMore(data['@odata.count'] > data.value.length + $skip);
      setImages([
        ...($skip > 0 ? images : []),
        ...map(data.value, (item) => {
          const image = {
            thumbnailSrc: item.Url,
            id: item.XandrAssetId,
            description: item.Description,
            imageSrc: item.Url,
            isSelected: false,
          };

          return image;
        }),
      ]);

      if ($skip === 0) {
        setIsLoadingFirstPage(false);
      }
    });
  }, [
    images, setImages, searchQuery, skip, setHasMore, setIsLoadingFirstPage, getInvestImages,
  ]);

  const loadNextPage = useCallback(() => {
    if (hasMore) {
      const newSkip = skip + PAGE_SIZE;
      setSkip(newSkip);
      loadImages({ $skip: newSkip });
    }
  }, [skip, setSkip, loadImages, hasMore]);

  const onSelectImage = useCallback((index) => {
    const newArr = [...images];
    const selectedImage = newArr[index];

    const selectionChange = { selectedImageId: selectedImage.id };

    if (enableMultipleSelection) {
      const newSelectionState = !selectedImage.isSelected;

      newArr[index].isSelected = newSelectionState;
      selectionChange.selectionState = newSelectionState;
    }

    setImages(newArr);
    onChangeImageIdSelection(selectionChange);
  }, [images, setImages, onChangeImageIdSelection, enableMultipleSelection]);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      setSkip(0);
      loadImages({
        $skip: 0,
      });
    }
  }, [setSkip, loadImages]);


  useEffect(() => {
    setSkip(0);
    loadImages({ $skip: 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classnames('invest-image-view', investImageRoot)}>
      <div className={classnames(searchFieldClasses.container, 'invest-image-searchbar')}>
        <TextField
          value={searchQuery}
          onChange={(ev, newValue) => setSearchQuery(newValue)}
          onKeyDown={onKeyDown}
          placeholder={i18n.getString(_TL_('Search by Invest creative Id'))}
          styles={{
            root: {
              width: '50%',
              display: 'inline-block',
            },
          }}
        />
        <IconButton
          iconProps={{ iconName: 'Search' }}
          className="search-button"
          onClick={() => {
            setSkip(0);
            loadImages({
              $skip: 0,
            });
          }}
        />
      </div>
      {
        isLoadingFirstPage ? <AssetLibrarySpinner useCustomSize /> :
        <FluentImagePicker
          className="invest-image-picker"
          images={images}
          pageSize={PAGE_SIZE}
          loadMoreImages={loadNextPage}
          onSelectImage={onSelectImage}
          hasMoreImages={hasMore}
          imageCellWidth={imageCellWidth}
          imageCellHeight={imageCellHeight}
        />
      }
    </div>
  );
};

InvestImageView.propTypes = {
  getInvestImages: PropTypes.func.isRequired,
  onChangeImageIdSelection: PropTypes.func.isRequired,
  enableMultipleSelection: PropTypes.bool,
  imageCellWidth: PropTypes.number,
  imageCellHeight: PropTypes.number,
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setImages: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  setHasMore: PropTypes.func.isRequired,
  skip: PropTypes.number.isRequired,
  setSkip: PropTypes.func.isRequired,
  isLoadingFirstPage: PropTypes.bool.isRequired,
  setIsLoadingFirstPage: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
};

InvestImageView.defaultProps = {
  enableMultipleSelection: true,
  imageCellWidth: undefined,
  imageCellHeight: undefined,
};
